<template>
  <div class="relative">
    <div v-if="isLoading">
      <UtilPlaceholder class="rounded-t" style="padding-top: 66.66%" />
      <div class="flex mt-2 mb-4">
        <UtilPlaceholder class="w-3/12 h-40 mr-2 rounded" />
        <UtilPlaceholder class="w-3/12 h-40 mr-2 rounded" />
        <UtilPlaceholder class="w-3/12 h-40 mr-2 rounded" />
        <UtilPlaceholder class="w-3/12 h-40 rounded" />
      </div>
    </div>

    <template v-else>
      <div class="flex gap-4 gallery-container" :class="classes.container">
        <UtilListingGalleryThumbnail
          :item="featured"
          :class="classes.featured"
          class="relative h-64 bg-black rounded-md featured-thumbnail"
          @click="onGalleryItemClick(0)"
        >
          <UtilListingVideo v-if="featured.type === 'video'" ref="featuredVideo" autoPlay :file="featured" />
        </UtilListingGalleryThumbnail>

        <div v-if="preview.length" :class="classes.preview" class="gap-4 preview-thumbnails-container">
          <UtilListingGalleryThumbnail
            v-for="(item, i) in preview"
            :key="`preview-${i}`"
            :item="item"
            :class="[classes.previewItems, classes[`previewItem${i + 1}`]]"
            @click="onGalleryItemClick(i + 1)"
          >
            <div
              v-if="i === 3 && isHidden"
              class="absolute z-20 flex items-center justify-center w-full h-full tracking-widest bg-black bg-opacity-50"
            >
              <span class="text-white">+{{ getStat('hidden') ? getStat('hidden').value : '' }}</span>
            </div>
          </UtilListingGalleryThumbnail>
        </div>
      </div>

      <div class="absolute top-0 left-0 z-20 flex items-center p-4 space-x-2">
        <span
          v-for="stat in getStats(['videos', 'images'], { removeEmpty: true })"
          :key="stat.value"
          class="flex items-center px-1 py-px space-x-1 text-sm font-medium text-white rounded-lg bg-brand-darker"
        >
          <FontAwesomeIcon :icon="stat.icon" />
          <span>{{ stat.value }}</span>
        </span>
      </div>

      <div v-if="isModalOpen" class="fixed inset-0 z-50 flex items-center justify-center w-full h-screen">
        <div
          class="absolute inset-0 z-10 w-full h-full bg-black bg-opacity-60"
          @click="onCloseButtonClick()"
        ></div>

        <div
          class="relative z-20 flex items-center w-full h-full max-w-5xl mx-auto bg-black shadow-2xl left-"
          :style="{ maxHeight: '520px' }"
        >
          <div class="absolute z-20 px-4 py-1 text-white bg-black bg-opacity-50 rounded-lg left-5 top-10">
            <span class="text-sm font-medium">{{ gallery.index + 1 }} of {{ items.length }}</span>
          </div>

          <UtilListingGalleryItem
            v-for="(item, i) in items"
            :item="item"
            :active="isItem(i)"
            :key="`gallery-${i}`"
            class="z-10 w-full h-full"
          />

          <button :class="[classes.buttons, 'left-5 -mt-6']" @click="onPrevButtonClick()" :style="{ top: '50%' }">
            <FontAwesomeIcon :icon="faChevronLeft" />
          </button>

          <button :class="[classes.buttons, 'top-10 right-5']" @click="onCloseButtonClick()">
            <FontAwesomeIcon :icon="faTimes" />
          </button>

          <button :class="[classes.buttons, 'right-5 -mt-6']" @click="onNextButtonClick()" :style="{ top: '50%' }">
            <FontAwesomeIcon :icon="faChevronRight" />
          </button>
        </div>
      </div>
    </template>

    <UtilScriptLoader
      scriptId="cloudflare-video"
      scriptUrl="https://embed.cloudflarestream.com/embed/sdk.latest.js"
    />
  </div>
</template>

<script>
import {
  faCamera,
  faMapMarker,
  faPlayCircle,
  faChevronRight,
  faChevronLeft,
  faTimes
} from '@fortawesome/pro-regular-svg-icons'

import UtilPlaceholder from '@/components/utils/placeholders/UtilPlaceholder'
import UtilListingGalleryThumbnail from './UtilListingGalleryThumbnail.vue'
import UtilListingVideo from './UtilListingVideo.vue'
import UtilListingGalleryItem from './UtilListingGalleryItem.vue'
import UtilScriptLoader from '@/components/utils/UtilScriptLoader.vue'

export default {
  name: 'UtilListingGallery',

  components: {
    UtilListingGalleryThumbnail,
    UtilListingGalleryItem,
    UtilPlaceholder,
    UtilListingVideo,
    UtilScriptLoader
  },
  props: {
    listing: { type: Object },
    images: { type: Array, default: () => [] },
    videos: { type: Array, default: () => [] },
    locations: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: true }
  },
  data() {
    return {
      isModalOpen: false,
      gallery: {
        index: 0
      },
      faChevronRight,
      faChevronLeft,
      faTimes
    }
  },
  watch: {
    isModalOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const video = this.$refs.featuredVideo
          if (video) {
            video.stopVideo()
          }
        })
      }
    }
  },
  computed: {
    items() {
      return [...this.videos, ...this.images, ...this.locations]
    },
    stats() {
      return [
        { key: 'videos', icon: faPlayCircle, value: this.videos.length },
        { key: 'images', icon: faCamera, value: this.images.length },
        { key: 'locations', icon: faMapMarker, value: this.locations.length },
        { key: 'hidden', icon: faCamera, value: this.items.length - 5 }
      ]
    },
    featured() {
      return this.items[0]
    },
    preview() {
      return this.items.slice(1, 5)
    },
    classes() {
      const count = this.items.length
      const classes = {
        buttons:
          'absolute w-10 h-10 text-white flex items-center justify-center flex-col rounded-full bg-black bg-opacity-50 z-20 hover:bg-brand-darker',
        container: '',
        featured: '',
        preview: '',
        previewItems: '',
        previewItem1: '',
        previewItem2: '',
        previewItem3: '',
        previewItem4: ''
      }
      switch (count) {
        case 1:
          classes.container = ''
          classes.featured = 'w-full h-full rounded-md'
          classes.preview = ''
          break
        case 2:
          classes.container = 'flex-col md:flex-row'
          classes.featured = 'w-full md:h-full'
          classes.preview = 'flex-auto flex w-full md:h-full  rounded-md'
          classes.previewItems = 'w-1/4  md:w-full'
          break
        case 3:
          classes.container = 'flex-col md:flex-row'
          classes.featured = 'w-full md:h-full'
          classes.preview = 'flex-auto flex w-full md:flex-col md:h-full rounded-md'
          classes.previewItems = 'w-1/4  md:w-full preview-3-height'
          break
        case 4:
          classes.container = 'flex-col md:flex-row'
          classes.featured = 'w-full md:h-full'
          classes.preview = 'flex-auto w-full grid grid-cols-3 md:grid-cols-2 rounded-md'
          classes.previewItem1 = 'md:col-span-full'
          break
        default:
          classes.container = 'flex-col md:flex-row'
          classes.featured = 'w-full md:h-full'
          classes.preview = 'flex-auto w-full grid grid-cols-4 md:grid-cols-2 md:h-full'
      }
      return classes
    },
    isHidden() {
      const hidden = this.getStat('hidden')
      return hidden && hidden.value > 0
    }
  },
  methods: {
    isItem(index) {
      return this.gallery.index === index
    },
    toItem(index) {
      const len = this.items.length
      this.gallery.index = (index + len) % len
    },
    getStat(key) {
      return this.stats.find(stat => key === stat.key)
    },
    getStats(keys, options = {}) {
      const filtered = this.stats.filter(stat => keys.includes(stat.key))
      if (options.removeEmpty) {
        return filtered.filter(stat => stat.value > 0)
      } else {
        return filtered
      }
    },
    onGalleryItemClick(index) {
      this.gallery.index = index
      this.isModalOpen = true
      console.log('open')
    },
    onCloseButtonClick() {
      this.isModalOpen = false
    },
    onPrevButtonClick() {
      this.toItem(this.gallery.index - 1)
    },
    onNextButtonClick() {
      this.toItem(this.gallery.index + 1)
    }
  },

  mounted() {
    document.addEventListener('keydown', event => {
      if (!this.isModalOpen) return

      switch (event.key) {
        case 'Escape':
          if (this.isModalOpen) {
            this.onCloseButtonClick()
          }
          break
        case 'ArrowLeft':
          if (this.isModalOpen) {
            this.onPrevButtonClick()
          }
          break
        case 'ArrowRight':
          if (this.isModalOpen) {
            this.onNextButtonClick()
          }
          break
      }
    })
  }
}
</script>

<style lang="scss" scoped>
@media (min-width: 768px) {
  .gallery-container {
    height: 520px;
  }
}

.featured-thumbnail {
  height: 350px;
}

@media (min-width: 768px) {
  .featured-thumbnail {
    height: 100%;
  }
}

@media (max-width: 768px) {
  .preview-thumbnails-container {
    height: 130px;
  }
}

@media (min-width: 768px) {
  .preview-3-height {
    height: 50%;
  }
}
</style>
